import React, { useState } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import './sentry'; // Initialize Sentry
import auth from './auth/auth0';
import { BrowserRouter as Router, Route, Routes, useNavigate } from "react-router-dom";
import AuthCallbackComponent from './auth/authCallback.component';
import App from './app/app.component';
import { AppGrowthBookProvider } from './providers/GrowthBookProvider';
import { PermissionsProvider } from './auth/PermissionsContext';
import { installConsoleForwarder } from './utils/consoleForwarder';
import logger, { registerBackend, setBackend } from './utils/logger';

const RootComponent = () => {
    // Install console forwarder (no-op on localhost; activates in dev/staging/prod envs)
    try {
        const v2Url = (import.meta && import.meta.env && import.meta.env.VITE_API_V2_URL) || undefined;
        if (v2Url) {
            const wrapped = installConsoleForwarder({ v2ApiUrl: v2Url, appVersion: (window).__APP_VERSION__ });
            if (wrapped) {
                // Register and activate the forwarding backend
                registerBackend('console', wrapped);
                setBackend('console');
            }
        }
    } catch (e) {}

    const [role, setRole] = useState(auth.getPacifyRole());
    const navigate = useNavigate();

    // If we're on the callback page, render the callback component
    if (window.location.pathname === '/callback') {
        return (
            <Routes>
                <Route 
                    path="/callback" 
                    element={
                        <AuthCallbackComponent onSuccess={() => {
                            auth.setNavigate(navigate);
                            auth.handleAuthentication(setRole);
                        }} />
                    } 
                />
            </Routes>
        );
    }

    // If we have a role, render the main app
    if (role) {
        return (
            <PermissionsProvider>
                <App />
            </PermissionsProvider>
        );
    }

    // Otherwise, redirect to login
    auth.login();
    return null;
};

const container = document.getElementById('root');
const root = createRoot(container);
root.render(
    <AppGrowthBookProvider>
        <Router>
            <RootComponent />
        </Router>
    </AppGrowthBookProvider>
);

// Unregister any existing service workers
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(function(registrations) {
    for(let registration of registrations) {
      registration.unregister();
    }
  });
}
